import { useState } from 'react'
import { Cookie, Settings2 } from 'lucide-react'
import { supabase } from '../lib/supabase'

const STORAGE_KEY = 'lentals_cookie_consent'
const CONSENT_VERSION = '2026-03-11'

interface ConsentState {
  analytics: boolean
  marketing: boolean
  functional: boolean
  consentId: string
  version: string
}

function getStoredConsent(): ConsentState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored) as ConsentState
    if (parsed.version !== CONSENT_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

function storeConsent(consent: ConsentState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
}

async function saveConsentToDb(consent: ConsentState): Promise<void> {
  try {
    const session = (await supabase.auth.getSession()).data.session
    await supabase.from('cookie_consents').insert({
      user_id: session?.user?.id ?? null,
      consent_id: consent.consentId,
      analytics: consent.analytics,
      marketing: consent.marketing,
      functional: consent.functional,
      user_agent: navigator.userAgent,
    })
  } catch {
    // Non-blocking — localStorage is the primary record
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(() => !getStoredConsent())
  const [showPreferences, setShowPreferences] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  if (!visible) return null

  const consentId = crypto.randomUUID()

  function accept(opts: { analytics: boolean; marketing: boolean }) {
    const consent: ConsentState = {
      analytics: opts.analytics,
      marketing: opts.marketing,
      functional: true,
      consentId,
      version: CONSENT_VERSION,
    }
    storeConsent(consent)
    saveConsentToDb(consent)
    setVisible(false)
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-4 sm:p-6">
      <div
        className="mx-auto max-w-lg rounded-2xl p-5 shadow-lg"
        style={{
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
        }}
      >
        {showPreferences ? (
          /* ─── Preferences view ─── */
          <>
            <div className="flex items-center gap-2 mb-4">
              <Settings2 className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
              <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                Cookie preferences
              </h3>
            </div>

            <div className="space-y-3 mb-4">
              {/* Essential — always on */}
              <label className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Essential</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Required for the site to function</p>
                </div>
                <input type="checkbox" checked disabled className="w-4 h-4 accent-[var(--color-accent)]" />
              </label>

              {/* Analytics */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Analytics</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Help us improve the experience</p>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={e => setAnalytics(e.target.checked)}
                  className="w-4 h-4 accent-[var(--color-accent)]"
                />
              </label>

              {/* Marketing */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Marketing</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Personalised recommendations</p>
                </div>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={e => setMarketing(e.target.checked)}
                  className="w-4 h-4 accent-[var(--color-accent)]"
                />
              </label>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowPreferences(false)}
                className="flex-1 py-2 text-sm font-medium rounded-xl transition"
                style={{ color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}
              >
                Back
              </button>
              <button
                onClick={() => accept({ analytics, marketing })}
                className="btn-primary flex-1 py-2 text-sm font-semibold rounded-xl transition"
              >
                Save preferences
              </button>
            </div>
          </>
        ) : (
          /* ─── Default banner ─── */
          <>
            <div className="flex items-start gap-3 mb-4">
              <Cookie className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
              <div>
                <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
                  We use cookies
                </h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  We use essential cookies to keep the site running. Analytics and marketing cookies are optional.
                  See our{' '}
                  <a href="/legal/privacy" className="underline" style={{ color: 'var(--color-accent)' }}>
                    Privacy Policy
                  </a>{' '}
                  for details.
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowPreferences(true)}
                className="flex-1 py-2 text-sm font-medium rounded-xl transition"
                style={{ color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}
              >
                Manage
              </button>
              <button
                onClick={() => accept({ analytics: false, marketing: false })}
                className="flex-1 py-2 text-sm font-medium rounded-xl transition"
                style={{ color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}
              >
                Essential only
              </button>
              <button
                onClick={() => accept({ analytics: true, marketing: true })}
                className="btn-primary flex-1 py-2 text-sm font-semibold rounded-xl transition"
              >
                Accept all
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
